import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authApi } from '../services/api';
import { AdminAccount } from '../services/adminAccountsService';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  avatar?: string;
  role: 'user' | 'admin' | 'master';
  isMaster?: boolean;
  masterRole?: 'PLATFORM_OWNER' | 'OPERATIONS_OFFICER' | 'BUSINESS_MANAGER';
  preferences?: {
    language: 'en' | 'ar';
    theme: 'light' | 'dark' | 'system';
    notifications: {
      email: boolean;
      push: boolean;
      sms: boolean;
      whatsapp: boolean;
    };
    currency: string;
    timezone: string;
  };
  createdAt?: Date;
  updatedAt?: Date;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: Partial<User> };

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  signup: (userData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
  }) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  
  // Admin-specific methods
  adminLogin: (email: string, password: string) => Promise<AdminAccount>;
  getCurrentAdmin: () => AdminAccount | null;
  switchToAdmin: (adminId: string) => AdminAccount | null;
  adminLogout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true
      };
    
    case 'AUTH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token
      };
    
    case 'AUTH_FAILURE':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        token: null
      };
    
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null
      };
    
    case 'UPDATE_USER':
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null
      };
    
    default:
      return state;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    token: null,
    isLoading: false,
    isAuthenticated: false
  });

  // Check for existing auth token on mount and validate with API
  useEffect(() => {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('admin_token');
    const adminData = localStorage.getItem('current_admin');
    
    if (token) {
      // If we have admin data, use it directly
      if (adminData) {
        try {
          const admin = JSON.parse(adminData);

          // Detect master from any of: explicit role, isMaster flag, or email pattern.
          // This mirrors useMasterAuth so a refreshed master session keeps full access.
          const email: string = admin.email || '';
          const isMasterUser =
            admin.role === 'master' ||
            admin.isMaster === true ||
            email.includes('master') ||
            email.includes('@eticket.team') ||
            email.includes('@platform.admin');

          // Stored payload uses a single `name` field (e.g. "Master Admin").
          // Split it so firstName/lastName render correctly in the dashboard.
          const rawName: string = admin.name || admin.firstName || 'Admin';
          const nameParts = String(rawName).split(' ');
          const firstName = admin.firstName || nameParts[0] || 'Admin';
          const lastName =
            admin.lastName !== undefined
              ? admin.lastName
              : nameParts.slice(1).join(' ') || '';

          dispatch({
            type: 'AUTH_SUCCESS',
            payload: {
              user: {
                id: admin.id,
                firstName,
                lastName,
                email: admin.email,
                role: isMasterUser ? 'master' : 'admin',
                isMaster: isMasterUser,
                masterRole:
                  admin.masterRole ||
                  (isMasterUser ? 'PLATFORM_OWNER' : undefined)
              },
              token
            }
          });
          return;
        } catch (error) {
          // If parsing fails, continue to validate with API
        }
      }
      
      // Validate token with API for regular users
      authApi.getCurrentUser()
        .then((user) => {
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: { user, token }
          });
        })
        .catch(() => {
          // Token is invalid, clear it
          localStorage.removeItem('auth_token');
          localStorage.removeItem('admin_token');
          localStorage.removeItem('current_admin');
          dispatch({ type: 'AUTH_FAILURE' });
        });
    }
  }, []);


  const login = async (email: string, password: string): Promise<void> => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      // Call actual API
      const response = await authApi.login({ email, password });
      
      // Store token in localStorage (API client will use it automatically)
      const token = response.data.accessToken;
      localStorage.setItem('auth_token', token);
      
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user: response.data.user, token }
      });
    } catch (error: any) {
      dispatch({ type: 'AUTH_FAILURE' });
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const signup = async (userData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
  }): Promise<void> => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      // Call actual API
      const response = await authApi.register(userData);
      
      // Store token in localStorage (API client will use it automatically)
      const token = response.data.accessToken;
      localStorage.setItem('auth_token', token);
      
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user: response.data.user, token }
      });
    } catch (error: any) {
      dispatch({ type: 'AUTH_FAILURE' });
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  };

  const logout = (): void => {
    // Call API to logout (optional - clears server-side session if any)
    authApi.logout().catch(() => {
      // Ignore errors, just clear local state
    });
    
    // Clear all authentication-related data from localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('current_admin');
    localStorage.removeItem('master_user');
    
    dispatch({ type: 'LOGOUT' });
  };

  const updateUser = (userData: Partial<User>): void => {
    dispatch({ type: 'UPDATE_USER', payload: userData });
    
    // Note: In a real app, you might want to sync this with the API
    // For now, we just update the local state
  };

  // Admin-specific methods
  const adminLogin = async (email: string, password: string): Promise<AdminAccount> => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      // Use the admin login API endpoint
      // Note: API client interceptor already returns response.data
      const response = await authApi.adminLogin({ email, password });
      
      // Check if response is successful
      if (!response || !response.success || !response.data) {
        throw new Error(response?.message || 'Invalid response from server');
      }
      
      // Store admin token
      const token = response.data.accessToken;
      if (!token) {
        throw new Error('No access token received from server');
      }
      
      localStorage.setItem('admin_token', token);
      localStorage.setItem('auth_token', token); // Also store as auth_token for API client
      localStorage.setItem('current_admin', JSON.stringify(response.data.user));
      
      // Update AuthContext state to reflect logged-in admin
      const adminUser = response.data.user;
      if (!adminUser) {
        throw new Error('No user data received from server');
      }
      
      // Check if this is a master user. Include email-pattern fallback so
      // platform/master emails are recognised even if the API omits the flag.
      const adminEmail: string = adminUser.email || '';
      const isMasterUser =
        adminUser.role === 'master' ||
        adminUser.isMaster === true ||
        adminEmail.includes('master') ||
        adminEmail.includes('@eticket.team') ||
        adminEmail.includes('@platform.admin');

      // Split name if it contains space (for master users)
      const nameParts = (adminUser.name || 'Admin').split(' ');
      const firstName = nameParts[0] || 'Admin';
      const lastName = nameParts.slice(1).join(' ') || '';

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: {
            id: adminUser.id,
            firstName,
            lastName,
            email: adminUser.email,
            role: isMasterUser ? 'master' : 'admin',
            isMaster: isMasterUser,
            masterRole: (adminUser as any).masterRole || (isMasterUser ? 'PLATFORM_OWNER' : undefined)
          },
          token
        }
      });
      
      // If master user, also store in a way that useMasterAuth can find it
      if (isMasterUser) {
        localStorage.setItem('master_user', JSON.stringify(adminUser));
      }
      
      return response.data.user as any; // Type conversion
    } catch (error: any) {
      dispatch({ type: 'AUTH_FAILURE' });
      const errorMessage = error?.message || error?.response?.data?.message || error?.response?.message || 'Admin login failed. Please try again.';
      console.error('Admin login error:', error);
      throw new Error(errorMessage);
    }
  };

  const getCurrentAdmin = (): AdminAccount | null => {
    const adminData = localStorage.getItem('current_admin');
    if (adminData) {
      try {
        return JSON.parse(adminData);
      } catch {
        return null;
      }
    }
    return null;
  };

  const switchToAdmin = (adminId: string): AdminAccount | null => {
    // This would need to be implemented with proper API support
    // For now, return null as this is a complex feature
    console.warn('switchToAdmin not yet implemented with API');
    return null;
  };

  const adminLogout = (): void => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('current_admin');
    localStorage.removeItem('auth_token');
    dispatch({ type: 'LOGOUT' });
  };

  const value: AuthContextType = {
    ...state,
    login,
    signup,
    logout,
    updateUser,
    adminLogin,
    getCurrentAdmin,
    switchToAdmin,
    adminLogout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 